// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Daily Catch  (data + time helpers for pages/DailyCatch.tsx)
// The shape of GET /api/public/drop (backend/app/schemas/drops.py) and the small formatters the page needs. Times are
// shown in India time (IST) whatever the viewer's device says — the harbour clock is what matters for "landed 5:18 AM"
// and the dispatch countdown.
// ─────────────────────────────────────────────────────────────────────────────
import { apiGet } from "./api";

export interface Landing {
  id: string;
  /** A catch id from data/seafood.ts; the site prices it from the menu. */
  itemId: string;
  landedAt: string;
  boat: string | null;
  spec: string | null;
  grade: string | null;
  packsTotal: number;
  packsLeft: number;
  soldOut: boolean;
  /** Dockside intake (staff-entered; no scale/lab hardware). Any of these may be unset. */
  vesselReg: string | null;
  grossKg: number | null;
  netKg: number | null;
  /** What ZenFresh paid the boat — a ledger record only, not a payment made through this system. */
  purchasePriceInr: number | null;
}

export interface DropInfo {
  label: string;
  dispatchAt: string | null;
  note: string | null;
  published: boolean;
}

export interface DropView {
  serverTime: string;
  /** Null (and no landings) when nothing is published. */
  drop: DropInfo | null;
  landings: Landing[];
  claimedPercent: number;
  /** Real totals: only landings staff weighed / priced are counted (see Landing above). */
  totalNetKg: number;
  totalProcurementInr: number;
}

export const fetchDrop = (signal?: AbortSignal) => apiGet<DropView>("/public/drop", signal);

/** "5:18 AM" in IST. */
export const formatLanded = (iso: string): string =>
  new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" })
    .format(new Date(iso))
    .replace(/\s?([ap])m$/i, (_, letter: string) => ` ${letter.toUpperCase()}M`);

/** "14:28" for under an hour, "1 h 05 min" beyond; null once the time has passed. */
export function formatCountdown(ms: number): string | null {
  if (ms <= 0) return null;
  const total = Math.ceil(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h} h ${String(m).padStart(2, "0")} min` : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Packs left at or below this is shown as "Only N left" in the alert colour. */
export const LOW_STOCK = 5;
